import { formatEther, formatUnits, parseEther, parseUnits } from 'ethers/lib/utils';
import { UserPositionRaw } from '../../../../hooks/web3/trade/useUserPositions';
import { useMemo } from 'react';
import ClosePositionPreview from '../ClosePositionPreview';
import { calculatePnl, formatNonNegative } from '../../../../helpers';
import useUserFee from '../../../../hooks/web3/useUserFee';
import useTokenStats from '../../../../hooks/web3/useTokenStats';
import { config } from '../../../../config/config';

type CloseMarketProps = {
    userPosition: UserPositionRaw | undefined;
    amount: string;
};

const CloseMarketPreview: React.FC<CloseMarketProps> = ({ userPosition, amount }) => {
    const { userFee } = useUserFee();
    const tokenStats = useTokenStats();

    const pnlValues = userPosition
        ? calculatePnl(
              userPosition,
              tokenStats?.[userPosition.tradePair.chartSymbol]?.price
          )
        : undefined;

    const removedPercentage = useMemo(
        () =>
            parseEther(amount || '0')
                .mul(parseEther('1'))
                .div(parseEther(userPosition?.volume || '1')),
        [amount, userPosition?.volume]
    );

    const isFullAmount = useMemo(
        () => removedPercentage.eq(parseEther('1')),
        [removedPercentage]
    );

    const previewSize = useMemo(
        () =>
            parseEther(userPosition?.volume || '1')
                .mul(parseEther('1').sub(removedPercentage))
                .div(parseEther('1')),
        [userPosition?.volume, removedPercentage]
    );

    const previewCollateral = useMemo(
        () =>
            parseEther(userPosition?.margin || '1')
                .mul(parseEther('1').sub(removedPercentage))
                .div(parseEther('1')),
        [userPosition?.margin, removedPercentage]
    );

    const previewPnL = useMemo(
        () =>
            parseUnits(pnlValues?.pnl || '1', config.priceFeedDecimal)
                .mul(
                    parseUnits('1', config.priceFeedDecimal).sub(
                        parseUnits(
                            removedPercentage.toString(),
                            config.priceFeedDecimal - 18
                        )
                    )
                )
                .div(parseUnits('1', config.priceFeedDecimal)),
        [pnlValues?.pnl, removedPercentage]
    );

    const closeFee = useMemo(
        () =>
            parseEther(userPosition?.volume || '0')
                .sub(previewSize)
                .mul(userFee || 0)
                .div(100_00),
        [userPosition?.volume, previewSize, userFee]
    );

    return (
        <ClosePositionPreview
            positionDetails={{
                tradePair: userPosition?.tradePair,
                bankruptcyPrice: {
                    value: userPosition?.bankruptcyPrice || '0',
                },
                collateral: {
                    value: userPosition?.margin || '0',
                    previewValue: removedPercentage.gt(0)
                        ? formatNonNegative(previewCollateral)
                        : undefined,
                },
                entryPrice: {
                    value: userPosition?.entryPrice || '0',
                },
                fees: {
                    value: undefined,
                    previewValue: closeFee.gt(0) ? formatEther(closeFee) : undefined,
                },
                isShort: userPosition?.isShort ?? false, // should never happen, but just in case
                leverage: {
                    value: userPosition?.leverage || '0',
                },
                markPrice: {
                    value:
                        tokenStats && userPosition?.tradePair
                            ? tokenStats?.[userPosition?.tradePair.chartSymbol]?.price ??
                              '0'
                            : '0',
                },
                pnl: {
                    value: pnlValues?.pnl,
                    previewValue: removedPercentage.gt(0)
                        ? isFullAmount
                            ? '0'
                            : pnlValues?.pnl
                            ? formatUnits(previewPnL, config.priceFeedDecimal)
                            : undefined
                        : undefined,
                },
                size: {
                    value: userPosition?.volume || '0',
                    previewValue: removedPercentage.gt(0)
                        ? formatNonNegative(previewSize)
                        : undefined,
                },
            }}
        />
    );
};

export default CloseMarketPreview;
